import { createContext, useContext, ReactNode, useMemo } from "react";
import { useAnalytics } from "@/modules/analytics/hooks/analytics.hook";
import { AnalyticsService } from "@/modules/analytics/services/analytics.service";
import { UserInfo } from "@/modules/analytics/types";

interface AnalyticsContextValue {
  analytics: AnalyticsService | null;
  ready: boolean;
  userInfo?: UserInfo | null;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined
);

export function AnalyticsProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { analytics, ready, userInfo } = useAnalytics();

  const contextValue = useMemo(
    () => ({ analytics, ready, userInfo }),
    [analytics, ready, userInfo]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error(
      "useAnalyticsContext must be used within an AnalyticsProvider"
    );
  }
  return ctx;
}
